import Metismenu from "metismenujs";
import React, { Component, useContext, useEffect, useState, useRef, useCallback } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Link, useHistory, useLocation } from "react-router-dom";
import { ThemeContext } from "../../../context/ThemeContext";
import { useFeatures } from "../../../context/FeatureContext";
import { useCapabilities } from "../../../context/CapabilityContext";
import { useTodos } from "../../components/AppsMenu/ToDo/TodoContext";
import { Badge, Modal } from "react-bootstrap";
import { BuildInfo } from "../../components/SharedComponents/BuildInfo";
import {
  LogOut as LogOutIcon,
  Megaphone,
  LayoutDashboard,
  BarChart3,
  Landmark,
  BookOpen,
  Receipt,
  ShoppingCart,
  Users,
  Settings as SettingsIcon,
  Building2,
  ChevronDown,
  UserCog,
  ClipboardCheck,
  Clock,
  Briefcase,
  FolderOpen,
  Inbox as InboxIcon,
  Package,
  Sparkles,
  CalendarCheck,
  Percent,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { selectCompany, setPreSelectedCompany, logout } from "../../../store/actions/AuthActions";
import { AiCreditsBar } from "../../components/Common/AiCreditsIndicator";
import { SidebarCollapseButton } from "./SidebarToggle";
import { useTerminology } from "../../../hooks/useTerminology";
import FeedbackWidget from "../../components/Common/FeedbackWidget";
import WhatsAppCommunityModal, { WhatsAppIcon } from "../../components/Common/WhatsAppCommunityModal";

class MM extends Component {
  componentDidMount() {
    this.$el = this.el;
    this.mm = new Metismenu(this.$el);
  }
  componentWillUnmount() {
    if (this.mm && typeof this.mm.dispose === "function") {
      this.mm.dispose();
    }
  }
  render() {
    return (
      <div className="mm-wrapper">
        <ul className="metismenu" ref={(el) => (this.el = el)}>
          {this.props.children}
        </ul>
      </div>
    );
  }
}

const SideBar = ({ companyId }) => {
  const { features, entitlements } = useFeatures();
  // Needs-attention badge for Bankhändelser (UX redesign Phase F): reads
  // the SAME 60s-polled ToDo aggregation the Att göra page uses — zero
  // extra requests. The banking contributor emits one count tile whose
  // count is exactly the home's "Att hantera" set.
  const { groups: todoGroups } = useTodos();
  const bankAttentionCount =
    todoGroups.find((g) => g.id === 'banking.attention')?.count || 0;
  // Phase 2.0 — capability-driven sidebar gating. The "Hantera …" links
  // below are the *manager/admin* surfaces and must be hidden from regular
  // employees who carry only `own` scope. Until /me/capabilities resolves,
  // hasScope returns false (fail-closed default) so the links don't
  // briefly flash before being hidden — see CapabilityContext.js. The
  // backend re-checks every request; this is the UX layer.
  const { hasScope } = useCapabilities();
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
  } = useContext(ThemeContext);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const currentCompanyId = useSelector((state) => state.auth.selectedCompanyId);
  const selectedCompany = useSelector((state) => state.auth.selectedCompany);
  const { companyTypeLabel } = useTerminology();

  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showBuildInfo, setShowBuildInfo] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showCommunity, setShowCommunity] = useState(false);
  const companyMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef(null);

  const handleLogoClick = useCallback((e) => {
    logoClickCount.current += 1;

    // Reset counter after 3 seconds of inactivity
    clearTimeout(logoClickTimer.current);
    logoClickTimer.current = setTimeout(() => {
      logoClickCount.current = 0;
    }, 3000);

    if (logoClickCount.current >= 10) {
      logoClickCount.current = 0;
      clearTimeout(logoClickTimer.current);
      setShowBuildInfo(true);
    }
  }, []);

  const hasFeature = (featureName) => {
    return features?.[featureName] === true || entitlements?.historicalFeatures?.[featureName] === true;
  };

  // "/<companyId>/reports/balance" → "reports-balance"; ids and uuids dropped
  // so per-record routes aggregate to their page instead of splintering.
  const feedbackSource =
    window.location.pathname
      .split("/")
      .slice(2)
      .filter((seg) => seg && !/^\d+$/.test(seg) && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg))
      .join("-") || "dashboard";

  /**
   * Section heading inside the nav list.
   *
   * Rendered as an <li> because the parent is a <ul> — a bare <div> there is
   * invalid markup and MetisMenu walks the child list. It carries no <a>, so
   * the collapsed-rail flyout code short-circuits on it (no submenu, no
   * .nav-text → openFor returns early) and CSS hides it in the rail entirely.
   *
   * `show` is required rather than defaulted: every group here is
   * feature-gated, and a heading left standing above zero visible links is
   * worse than no heading at all.
   */
  const NavSection = ({ label, show }) =>
    show ? (
      <li className="nav-section-label" aria-hidden="true">
        <span>{label}</span>
      </li>
    ) : null;

  /**
   * Inkorg sidebar entry. (Item-count badge intentionally omitted.)
   */
  const InboxNavItem = ({ companyId, active }) => (
    <li className={active ? "mm-active" : ""}>
      <Link to={`/${companyId}/inbox`}>
        <InboxIcon className="nav-icon" size={20} strokeWidth={1.75} />
        <span className="nav-text">Inkorg</span>
      </Link>
    </li>
  );

  // D1-4 — follow the URL, which is the authority for which company is on
  // screen. The switcher renders `selectedCompany` from Redux, and only
  // `selectCompany` (this component's own click handler) ever wrote it, so
  // navigating straight to another company's URL rendered that company's data
  // under the previous company's name — with no way to tell which one you were
  // editing. `setPreSelectedCompany` updates the context WITHOUT navigating;
  // persisting the choice stays with the click handler, because a URL visit is
  // not necessarily a change of the company you work in.
  useEffect(() => {
    const urlCompanyId = location.pathname.split('/')[1];
    if (!urlCompanyId || urlCompanyId === currentCompanyId) return;
    const companyData = user?.companies?.[urlCompanyId];
    // Unknown segment (login, create-company, …) or a company that is not the
    // user's: not ours to decide — the route guards own that.
    if (!companyData) return;
    dispatch(setPreSelectedCompany(urlCompanyId, companyData.companyName));
  }, [location.pathname, currentCompanyId, user, dispatch]);

  const onCompanyChange = (newCompanyId) => {
    const companyData = user?.companies?.[newCompanyId];
    if (companyData) {
      dispatch(
        selectCompany(
          newCompanyId,
          companyData.companyName,
          companyData.companyRegNo,
          history
        )
      );
      setShowCompanyMenu(false);
    }
  };

  const onLogout = () => {
    dispatch(logout(history));
  };

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (companyMenuRef.current && !companyMenuRef.current.contains(e.target)) {
        setShowCompanyMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    const unlisten = history.listen(() => {
      setShowCompanyMenu(false);
      setShowUserMenu(false);
    });
    return unlisten;
  }, [history]);

  // Collapsed-rail flyouts.
  //
  // When the sidebar is collapsed the nav area still scrolls (so the bottom
  // items stay reachable) and therefore clips its children. A submenu rendered
  // with position:absolute inside it would be cut off, so instead we promote it
  // to a fixed popover anchored to the hovered icon at hover time — fixed
  // escapes the clip entirely. Items without a submenu get a label tooltip so
  // the icon-only rail is never a row of unlabelled glyphs.
  //
  // A short close delay + flush placement (popover left edge == rail right
  // edge, no gap) means the cursor can travel onto the popover without it
  // vanishing — the old hover-only CSS flyout was nearly impossible to click.
  useEffect(() => {
    const nav = document.querySelector(".dlabnav .metismenu");
    if (!nav) return undefined;

    const isCollapsed = () =>
      document.getElementById("main-wrapper")?.classList.contains("menu-toggle");

    const tooltip = document.createElement("div");
    tooltip.className = "sidebar-flyout-tooltip";
    document.body.appendChild(tooltip);

    let hideTimer = null;
    let openUl = null;
    let activeLi = null;

    const clearHideTimer = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    };

    const closeFlyout = () => {
      if (openUl) {
        openUl.classList.remove("mm-flyout-open");
        openUl.style.top = "";
        openUl.style.left = "";
        openUl.style.bottom = "";
        openUl = null;
      }
      tooltip.classList.remove("show");
      activeLi = null;
    };

    const openFor = (li) => {
      if (!isCollapsed()) return;
      clearHideTimer();
      closeFlyout();
      activeLi = li;

      const anchor = li.querySelector(":scope > a") || li;
      const rect = anchor.getBoundingClientRect();
      const submenu = li.querySelector(":scope > ul");

      if (submenu) {
        submenu.style.left = `${Math.round(rect.right)}px`;
        submenu.style.top = `${Math.round(rect.top)}px`;
        submenu.style.bottom = "auto";
        submenu.classList.add("mm-flyout-open");
        openUl = submenu;
        // Flip upward if it would run off the bottom of the viewport.
        const menuRect = submenu.getBoundingClientRect();
        if (menuRect.bottom > window.innerHeight - 8) {
          submenu.style.top = "auto";
          submenu.style.bottom = "8px";
        }
      } else {
        // Prefer an explicit data-label (set when .nav-text carries extra
        // markup like a Beta badge whose text shouldn't leak into the tooltip).
        const label =
          anchor.getAttribute?.("data-label") ||
          li.querySelector(".nav-text")?.textContent?.trim();
        if (!label) return;
        tooltip.textContent = label;
        tooltip.style.top = `${Math.round(rect.top + rect.height / 2)}px`;
        tooltip.style.left = `${Math.round(rect.right)}px`;
        tooltip.classList.add("show");
      }
    };

    const scheduleClose = () => {
      clearHideTimer();
      hideTimer = setTimeout(closeFlyout, 180);
    };

    // mouseenter/leave don't bubble, so bind per top-level <li>. A
    // MutationObserver rebinds when feature flags add/remove menu entries.
    let bound = [];
    const unbind = () => {
      bound.forEach(({ li, enter, leave }) => {
        li.removeEventListener("mouseenter", enter);
        li.removeEventListener("mouseleave", leave);
      });
      bound = [];
    };
    const bind = () => {
      unbind();
      Array.from(nav.children)
        .filter((el) => el.tagName === "LI" && !el.classList.contains("nav-section-label"))
        .forEach((li) => {
          const enter = () => openFor(li);
          const leave = scheduleClose;
          li.addEventListener("mouseenter", enter);
          li.addEventListener("mouseleave", leave);
          bound.push({ li, enter, leave });
        });
    };

    bind();
    const observer = new MutationObserver(bind);
    observer.observe(nav, { childList: true });

    // Keep an open popover glued to its icon while the rail scrolls / window
    // resizes (fixed positioning won't follow on its own). Update coordinates
    // only — don't re-toggle the class, or the entrance animation replays.
    const reposition = () => {
      if (!activeLi || !isCollapsed()) return;
      const anchor = activeLi.querySelector(":scope > a") || activeLi;
      const rect = anchor.getBoundingClientRect();
      if (openUl) {
        openUl.style.left = `${Math.round(rect.right)}px`;
        openUl.style.top = `${Math.round(rect.top)}px`;
        openUl.style.bottom = "auto";
        const menuRect = openUl.getBoundingClientRect();
        if (menuRect.bottom > window.innerHeight - 8) {
          openUl.style.top = "auto";
          openUl.style.bottom = "8px";
        }
      } else {
        tooltip.style.top = `${Math.round(rect.top + rect.height / 2)}px`;
        tooltip.style.left = `${Math.round(rect.right)}px`;
      }
    };
    const scroller = document.querySelector(".sidebar-nav-scroll");
    scroller?.addEventListener("scroll", reposition, { passive: true });
    window.addEventListener("resize", reposition);

    return () => {
      observer.disconnect();
      unbind();
      clearHideTimer();
      scroller?.removeEventListener("scroll", reposition);
      window.removeEventListener("resize", reposition);
      tooltip.remove();
    };
  }, []);

  // Sidebar collapse/expand
  const toggleSidebar = () => {
    const wrapper = document.querySelector("#main-wrapper");
    if (wrapper) {
      wrapper.classList.toggle("menu-toggle");
    }
  };

  const path = window.location.pathname.split("/").pop();

  // Active menu groups
  const dashBoard = ["dashboard", "home"];
  const reports = ["vatreport", "balansreport", "resultatreport", "huvudbok", "asset-register", "asset-note"];
  // Creation is an action inside the Verifikat workspace, not a separate
  // navigation destination. Keep the single link active while either the
  // guided, manual, or legacy creation route is open.
  const bookkeeping = [
    "verifications",
    "create-bookkeeping-entry",
    "manual-bookkeeping-entry",
    "newverification",
  ];
  const bank = ["bank", "bankstatements", "bank-accounts", "skattekonto"];
  const factoring = ["invoices", "create-invoices", "customers", "articles"];
  const purchaseandorders = [
    "supplier-invoices",
    "create-supplier-invoices",
    "suppliers",
  ];
  // Tillgångar is a top-level item; deep routes (/assets/:assetId,
  // /assets/tax) end in ids/other segments, so match on the pathname
  // instead of the last-segment `path` value.
  const assetsActive = /\/assets(\/|$)/.test(window.location.pathname);
  // Bokslut — deep routes carry a fiscal-year id as the last segment, so
  // match on the pathname (same reasoning as assetsActive above).
  const yearEndActive = /\/year-end(\/|$)/.test(window.location.pathname);
  const taxPlanningActive = /\/tax-planning(\/|$)/.test(window.location.pathname);
  const personell = ["employees", "payroll/periods", "payroll/semester", "schedule"];

  // User initials for avatar
  const getInitials = () => {
    if (!user) return "?";
    const first = user.firstName?.[0] || "";
    const last = user.lastName?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const getUserDisplayName = () => {
    if (!user) return "";
    return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "";
  };

  const companyCount = user?.companies ? Object.keys(user.companies).length : 0;

  return (
    <div
      className={`dlabnav ${iconHover} ${
        sidebarposition?.value === "fixed" &&
        sidebarLayout?.value === "horizontal" &&
        headerposition?.value === "static"
          ? "fixed"
          : ""
      }`}
    >
      {/* Logo area */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-link" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <svg
            className="logo-abbr"
            width="28"
            height="28"
            viewBox="60.00 104.42 79.66 79.66"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g transform="rotate(18 -506.669 274.413)">
              <rect width="18" height="60" y="-60" fill="#bbf7d0" rx="4" />
              <rect width="18" height="60" y="-60" fill="none" stroke="#86efac" rx="4" />
              <rect width="14" height="1.5" x="2" y="-47" fill="#16a34a" rx="1" />
              <rect width="10" height="1" x="2" y="-44" fill="#16a34a" rx="1" />
              <rect width="12" height="1" x="2" y="-41" fill="#16a34a" rx="1" />
            </g>
            <g transform="rotate(18 -495.669 343.864)">
              <rect width="18" height="60" y="-60" fill="#e9d5ff" rx="4" />
              <rect width="18" height="60" y="-60" fill="none" stroke="#d8b4fe" rx="4" />
              <rect width="14" height="1.5" x="2" y="-47" fill="#9333ea" rx="1" />
              <rect width="8" height="1" x="2" y="-44" fill="#9333ea" rx="1" />
              <rect width="11" height="1" x="2" y="-41" fill="#9333ea" rx="1" />
            </g>
            <g transform="rotate(18 -484.669 413.315)">
              <rect width="18" height="60" y="-60" fill="#bfdbfe" rx="4" />
              <rect width="18" height="60" y="-60" fill="none" stroke="#93c5fd" rx="4" />
              <rect width="14" height="1.5" x="2" y="-47" fill="#1d4ed8" rx="1" />
              <rect width="9" height="1" x="2" y="-44" fill="#1d4ed8" rx="1" />
              <rect width="12" height="1" x="2" y="-41" fill="#1d4ed8" rx="1" />
            </g>
          </svg>
          <span className="sidebar-brand-title">
            Ledger<span className="brand-title-light">Loop</span>
          </span>
        </div>

        <SidebarCollapseButton onClick={toggleSidebar} />
      </div>

      {/* Scrollable navigation */}
      <PerfectScrollbar className="sidebar-nav-scroll">
        <MM className="metismenu" id="menu">
          {/* Menu order is intentional (set with the product owner): a short
              unlabelled "what needs me now" cluster on top, then the nav is
              grouped by money-flow — what comes in (Försäljning), what goes
              out (Inköp), the ledger that records both (Bokföring), then
              people and everything else. Keep new entries inside the group
              they belong to; a heading with nothing under it is worse than
              no heading, so every <NavSection> carries the same feature
              gate(s) as the links beneath it. */}

          {/* Single direct link — Översikt's only child was itself, so no
              submenu. Highlights on dashboard/home (see dashBoard array). */}
          <li className={`${dashBoard.includes(path) ? "mm-active" : ""}`}>
            <Link to={`/${companyId}/dashboard`}>
              <LayoutDashboard className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Översikt</span>
            </Link>
          </li>

          {/* Att göra — admin aggregated todo list. */}
          <li className={`${path === "todos" ? "mm-active" : ""}`}>
            <Link to={`/${companyId}/todos`}>
              <ClipboardCheck className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Att göra</span>
            </Link>
          </li>

          {/* AI-assistent — its own top-level surface (chat with Finn & the
              agent team spans bookkeeping, invoices, payroll — it isn't a
              Bokföring sub-feature). data-label feeds the collapsed-rail
              tooltip so the Beta badge text doesn't leak into it. */}
          {hasFeature("ai_assistant") && (
            <li className={`${path === "ai-assistant" ? "mm-active" : ""}`}>
              <Link
                to={`/${companyId}/ai-assistant`}
                data-label="AI-assistent"
              >
                <Sparkles className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text nav-text-badged">
                  AI-assistent
                  <Badge bg="primary" className="ms-auto" style={{ fontSize: "10px" }}>
                    Beta
                  </Badge>
                </span>
              </Link>
            </li>
          )}

          <NavSection label="Försäljning" show={["invoicing", "invoicing_quotes", "customer_management", "product_catalog"].some(hasFeature)} />

          {["invoicing", "invoicing_quotes", "customer_management", "product_catalog"].some(hasFeature) && (
            <li className={`${factoring.includes(path) ? "mm-active" : ""}`}>
              <Link className="has-arrow" to="#">
                <Receipt className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Fakturering och offerter</span>
              </Link>
              <ul>
                {hasFeature("invoicing") && (<li>
                  <Link
                    className={`${path === "invoices" ? "mm-active" : ""}`}
                    to={`/${companyId}/invoices`}
                  >
                    Kundfakturor
                  </Link>
                </li>)}
                {/* Offerter hidden until the quotes module gets the same
                    editor as invoices (2026-09-09). Re-enable by uncommenting;
                    the routes in jsx/index.js are commented out alongside. */}
                {/* {hasFeature("invoicing_quotes") && (
                  <li>
                    <Link
                      className={`${path === "quotes" ? "mm-active" : ""}`}
                      to={`/${companyId}/quotes`}
                    >
                      Offerter
                    </Link>
                  </li>
                )} */}
                {hasFeature("customer_management") && (<li>
                  <Link
                    className={`${path === "customers" ? "mm-active" : ""}`}
                    to={`/${companyId}/customers`}
                  >
                    Kunder
                  </Link>
                </li>)}
                {hasFeature("product_catalog") && (<li>
                  <Link
                    className={`${path === "articles" ? "mm-active" : ""}`}
                    to={`/${companyId}/articles`}
                  >
                    Artiklar
                  </Link>
                </li>)}
              </ul>
            </li>
          )}

          <NavSection label="Inköp" show={["inbox", "supplier_invoices", "supplier_management", "expense_management"].some(hasFeature)} />

          {hasFeature("inbox") && (
            <InboxNavItem companyId={companyId} active={path === "inbox"} />
          )}

          {(hasFeature("supplier_invoices") || hasFeature("supplier_management")) && (
            <li className={`${purchaseandorders.includes(path) ? "mm-active" : ""}`}>
              <Link className="has-arrow" to="#">
                <ShoppingCart className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Leverantörer och inköp</span>
              </Link>
              <ul>
                {hasFeature("supplier_invoices") && (<li>
                  <Link
                    className={`${
                      path === "supplier-invoices" ? "mm-active" : ""
                    }`}
                    to={`/${companyId}/supplier-invoices`}
                  >
                    Leverantörsfakturor
                  </Link>
                </li>)}
                {hasFeature("supplier_management") && (<li>
                  <Link
                    className={`${path === "suppliers" ? "mm-active" : ""}`}
                    to={`/${companyId}/suppliers`}
                  >
                    Leverantörer
                  </Link>
                </li>)}
              </ul>
            </li>
          )}

          {/* Utlägg */}
          {hasFeature("expense_management") && (
            <li className={`${window.location.pathname.includes("/expenses") ? "mm-active" : ""}`}>
              <Link className="has-arrow" to="#">
                <Receipt className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Utlägg</span>
              </Link>
              <ul>
                <li>
                  <Link
                    className={`${window.location.pathname.endsWith("/expenses") ? "mm-active" : ""}`}
                    to={`/${companyId}/expenses`}
                  >
                    Mina utlägg
                  </Link>
                </li>
                {/* Phase 2.0 — admin-tier-only surface. §4.2 forbids
                    manager-as-approver delegation for expenses (downstream
                    representation §8 / traktamente / momsavdrag tax
                    implications), so we gate on the strict 'all' rank
                    which expense.approve only grants to Admin / Owner /
                    payroll_admin / hr_admin. Managers with direct reports
                    do NOT see this link, even if they manage every
                    employee in the tenant. Backend mirror: requireExpense-
                    AdminTier in expenses/utils/authorize.js. */}
                {hasScope('expense', 'approve', 'all') && (
                  <li>
                    <Link
                      className={`${window.location.pathname.includes("/expenses/approvals") ? "mm-active" : ""}`}
                      to={`/${companyId}/expenses/approvals`}
                      title="Tidigare Godkännande"
                    >
                      Hantera utlägg
                    </Link>
                  </li>
                )}
              </ul>
            </li>
          )}

          {/* Always shown: Tillgångar and Rapporter carry no feature gate,
              so this group can never be empty. */}
          <NavSection label="Bokföring" show={["bookkeeping", "bank_reconciliation", "tax_account", "year_end", "tax_planning", "asset_management", "vat_reporting", "accounting_reports"].some(hasFeature)} />

          {hasFeature("bookkeeping") && (
            <li className={`${bookkeeping.includes(path) ? "mm-active" : ""}`}>
              <Link to={`/${companyId}/verifications`}>
                <BookOpen className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Verifikat</span>
              </Link>
            </li>
          )}

          {(hasFeature("bank_reconciliation") || hasFeature("tax_account")) && (
            <li className={`${bank.includes(path) ? "mm-active" : ""}`}>
              <Link className="has-arrow" to="#">
                <Landmark className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Bank & skatt</span>
              </Link>
              <ul>
                {hasFeature("bank_reconciliation") && (<li>
                  {/* Bankhändelser — the merged queue + ledger home (UX
                      redesign Phase C). The old separate "Matchning" item
                      is gone; /matching redirects here. The count badge is
                      the needs-attention set (Phase F). */}
                  <Link
                    className={`${path === "bankstatements" || path === "matching" ? "mm-active" : ""}`}
                    to={`/${companyId}/bankstatements`}
                  >
                    Bankhändelser
                    {bankAttentionCount > 0 && (
                      <span
                        style={{
                          marginLeft: 8,
                          background: "var(--rgba-primary-1)",
                          color: "var(--primary)",
                          borderRadius: 999,
                          padding: "0 7px",
                          fontSize: 11,
                          fontWeight: 600,
                          lineHeight: "18px",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      >
                        {bankAttentionCount > 99 ? "99+" : bankAttentionCount}
                      </span>
                    )}
                  </Link>
                </li>)}
                {hasFeature("tax_account") && (<li>
                  <Link
                    className={`${path === "skattekonto" ? "mm-active" : ""}`}
                    to={`/${companyId}/skattekonto`}
                  >
                    Skattekonto
                  </Link>
                </li>)}
                {hasFeature("bank_reconciliation") && (<li>
                  <Link
                    className={`${path === "bank-accounts" ? "mm-active" : ""}`}
                    to={`/${companyId}/bank-accounts`}
                  >
                    Konton
                  </Link>
                </li>)}
              </ul>
            </li>
          )}

          {hasFeature("year_end") && (<li className={`${yearEndActive ? "mm-active" : ""}`}>
            <Link to={`/${companyId}/year-end`}>
              <CalendarCheck className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Bokslut</span>
            </Link>
          </li>)}

          {hasFeature("tax_planning") && (<li className={`${taxPlanningActive ? "mm-active" : ""}`}>
            <Link to={`/${companyId}/tax-planning`}>
              <Percent className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Skatteplanering</span>
            </Link>
          </li>)}

          {/* Tillgångar — anläggningsregistret + avskrivningar. Universal
              feature (no requiredFeature and no hasFeature gate): every
              company type has assets, and the premium tax step gates
              server-side (403 → upgrade card). Top-level next to the
              ledger work, not under Leverantörer — assets can also come
              from imports and manual verifikat. */}
          {hasFeature("asset_management") && (<li className={`${assetsActive ? "mm-active" : ""}`}>
            <Link to={`/${companyId}/assets`}>
              <Package className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Tillgångar</span>
            </Link>
          </li>)}

          <li className={`${reports.includes(path) ? "mm-active" : ""}`}>
            <Link className="has-arrow" to="#">
              <BarChart3 className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Rapporter</span>
            </Link>
            <ul>
              {hasFeature("vat_reporting") && (<li>
                <Link
                  className={`${path === "vatreport" ? "mm-active" : ""}`}
                  to={`/${companyId}/vat-periods`}
                >
                  Momsrapport
                </Link>
              </li>)}
              {hasFeature("vat_reporting") && (<li>
                <Link
                  className={`${path === "periodisk-sammanstallning" ? "mm-active" : ""}`}
                  to={`/${companyId}/periodisk-sammanstallning`}
                >
                  Periodisk sammanställning
                </Link>
              </li>)}
              {hasFeature("accounting_reports") && (<li>
                <Link
                  className={`${path === "balansreport" ? "mm-active" : ""}`}
                  to={`/${companyId}/reports/balance`}
                >
                  Balansrapport
                </Link>
              </li>)}
              {hasFeature("accounting_reports") && (<li>
                <Link
                  className={`${path === "resultatreport" ? "mm-active" : ""}`}
                  to={`/${companyId}/reports/result`}
                >
                  Resultatrapport
                </Link>
              </li>)}
              {hasFeature("accounting_reports") && (<li>
                <Link
                  className={`${path === "huvudbok" ? "mm-active" : ""}`}
                  to={`/${companyId}/reports/mainledger`}
                >
                  Huvudbok
                </Link>
              </li>)}
              {hasFeature("asset_management") && (<li>
                <Link
                  className={`${path === "asset-register" ? "mm-active" : ""}`}
                  to={`/${companyId}/reports/asset-register`}
                >
                  Anläggningsregister
                </Link>
              </li>)}
              {hasFeature("asset_tax_optimizer") && (<li>
                <Link
                  className={`${path === "asset-note" ? "mm-active" : ""}`}
                  to={`/${companyId}/reports/asset-note`}
                >
                  Tillgångsnot
                </Link>
              </li>)}
            </ul>
          </li>

          <NavSection label="Personal" show={["personell_management", "payroll", "time_tracking"].some(hasFeature)} />

          {(hasFeature("personell_management") || hasFeature("payroll")) && (
            <li className={`${personell.includes(path) ? "mm-active" : ""}`}>
              <Link className="has-arrow" to="#">
                <Users className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Personal</span>
              </Link>
              <ul>
                {hasFeature("personell_management") && (<li>
                  <Link
                    className={`${path === "employees" ? "mm-active" : ""}`}
                    to={`/${companyId}/employees`}
                  >
                    Anställda
                  </Link>
                </li>)}
                {hasFeature("payroll") && (<li>
                  <Link
                    className={`${path === "periods" ? "mm-active" : ""}`}
                    to={`/${companyId}/payroll/periods`}
                  >
                    Löner
                  </Link>
                </li>)}
                {hasFeature("payroll") && (<li>
                  <Link
                    className={`${window.location.pathname.includes("/payroll/semester") ? "mm-active" : ""}`}
                    to={`/${companyId}/payroll/semester`}
                  >
                    Semester
                  </Link>
                </li>)}
              </ul>
            </li>
          )}

          {/* Tid */}
          {hasFeature("time_tracking") && (
            <li className={`${window.location.pathname.includes("/time-reporting") ? "mm-active" : ""}`}>
              <Link className="has-arrow" to="#">
                <Clock className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Tid</span>
              </Link>
              <ul>
                <li>
                  <Link
                    className={`${window.location.pathname.endsWith("/time-reporting") || window.location.pathname.includes("/time-reporting/timesheet") ? "mm-active" : ""}`}
                    to={`/${companyId}/time-reporting`}
                  >
                    Min tid
                  </Link>
                </li>
                {/* Phase 2.0 — manager/admin surface only. hasScope rank-aware:
                    'team' grants pass for both team-scope (managers) and
                    all-scope (admin tier). Backend re-checks via
                    requireAdmin / assertCanApprovePeriod; this is the UX
                    affordance. */}
                {hasScope('time', 'approve', 'team') && (
                  <li>
                    <Link
                      className={`${window.location.pathname.includes("/time-reporting/approval") ? "mm-active" : ""}`}
                      to={`/${companyId}/time-reporting/approval`}
                      title="Tidigare Godkännande"
                    >
                      Hantera tidrapporter
                    </Link>
                  </li>
                )}
                {/* Tenant-wide analytics — Admin ∨ Owner ∨ hr_admin ∨ payroll_admin.
                    Mirrors backend requireReportViewer; non-tier callers
                    previously saw the link, clicked through, and were greeted
                    with "HR role required for this action". hasScope returns
                    false fail-closed when /me/capabilities is still loading,
                    so the link doesn't flash for ineligible users on first
                    paint. */}
                {hasScope('time', 'report', 'all') && (
                  <li>
                    <Link
                      className={`${window.location.pathname.includes("/time-reporting/reports") ? "mm-active" : ""}`}
                      to={`/${companyId}/time-reporting/reports`}
                    >
                      Rapporter
                    </Link>
                  </li>
                )}
                {/* Datarensning (retention/GDPR purge) still hidden — admin-only
                    surface, not part of the daily flow. */}
              </ul>
            </li>
          )}

          <NavSection label="Övrigt" show={hasFeature("project_management") || hasFeature("document_management")} />

          {/* Projekt */}
          {hasFeature("project_management") && (
            <li className={`${window.location.pathname.includes("/projects") ? "mm-active" : ""}`}>
              <Link
                to={`/${companyId}/projects`}
              >
                <Briefcase className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Projekt</span>
              </Link>
            </li>
          )}

          {/* Dokument */}
          {hasFeature("document_management") && (
            <li className={`${window.location.pathname.includes("/documents") ? "mm-active" : ""}`}>
              <Link
                to={`/${companyId}/documents`}
              >
                <FolderOpen className="nav-icon" size={20} strokeWidth={1.75} />
                <span className="nav-text">Dokument</span>
              </Link>
            </li>
          )}

          {/* Single direct link — company settings is the only settings page,
              so no submenu. Highlights on any /settings* sub-section URL. */}
          <li className={`${window.location.pathname.includes("/settings") ? "mm-active" : ""}`}>
            <Link to={`/${companyId}/settings`}>
              <SettingsIcon className="nav-icon" size={20} strokeWidth={1.75} />
              <span className="nav-text">Inställningar</span>
            </Link>
          </li>
        </MM>
      </PerfectScrollbar>

      {/* Bottom section — user, company, credits */}
      <div className="sidebar-bottom">
        {/* AI Credits */}
        <div className="sidebar-credits">
          <Link to={`/${companyId}/settings/subscription`} style={{ textDecoration: "none" }}>
            <AiCreditsBar showLabel={true} />
          </Link>
        </div>

        {/* Company selector */}
        {companyCount > 1 && (
          <div className="sidebar-company" ref={companyMenuRef}>
            <button
              className="sidebar-company-btn"
              onClick={() => setShowCompanyMenu(!showCompanyMenu)}
            >
              <span className="sidebar-company-icon">
                <Building2 size={16} strokeWidth={1.75} />
              </span>
              <span className="sidebar-company-name">
                {selectedCompany?.companyName || "Välj företag"}
              </span>
              {companyTypeLabel && (
                <span className="sidebar-company-type-badge">
                  {companyTypeLabel}
                </span>
              )}
              <ChevronDown
                size={14}
                strokeWidth={1.75}
                className={`sidebar-company-arrow ${showCompanyMenu ? "open" : ""}`}
              />
            </button>

            {showCompanyMenu && (
              <div className="sidebar-company-menu">
                {user &&
                  user.companies &&
                  Object.entries(user.companies).map(([id, data]) => (
                    <button
                      key={id}
                      className={`sidebar-company-option ${
                        id === currentCompanyId ? "active" : ""
                      }`}
                      onClick={() => onCompanyChange(id)}
                    >
                      {data.companyName}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Single company display */}
        {companyCount === 1 && (
          <div className="sidebar-company-single">
            <span className="sidebar-company-icon">
              <Building2 size={16} strokeWidth={1.75} />
            </span>
            <span className="sidebar-company-name">
              {selectedCompany?.companyName || ""}
            </span>
            {companyTypeLabel && (
              <span className="sidebar-company-type-badge">
                {companyTypeLabel}
              </span>
            )}
          </div>
        )}

        {/* User section */}
        <div className="sidebar-user" ref={userMenuRef}>
          <button
            className="sidebar-user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {(user?.avatarUrl || user?.photoUrl) ? (
              <img
                className="sidebar-user-avatar sidebar-user-avatar-img"
                src={user.avatarUrl || user.photoUrl}
                alt={getUserDisplayName()}
              />
            ) : (
              <div className="sidebar-user-avatar">{getInitials()}</div>
            )}
            <div className="sidebar-user-details">
              <span className="sidebar-user-name">{getUserDisplayName()}</span>
              {user?.email && (
                <span className="sidebar-user-email">{user.email}</span>
              )}
            </div>
          </button>
          <button
            className="sidebar-logout-btn"
            onClick={onLogout}
            title="Logga ut"
          >
            <LogOutIcon size={16} strokeWidth={1.75} />
          </button>

          {showUserMenu && (
            <div className="sidebar-user-menu">
              <div className="sidebar-user-menu-header">
                {(user?.avatarUrl || user?.photoUrl) ? (
              <img
                className="sidebar-user-avatar sidebar-user-avatar-img"
                src={user.avatarUrl || user.photoUrl}
                alt={getUserDisplayName()}
              />
            ) : (
              <div className="sidebar-user-avatar">{getInitials()}</div>
            )}
                <div className="sidebar-user-menu-info">
                  <span className="sidebar-user-name">{getUserDisplayName()}</span>
                  {user?.email && (
                    <span className="sidebar-user-email">{user.email}</span>
                  )}
                </div>
              </div>
              <div className="sidebar-user-menu-divider" />
              <Link
                to={`/${companyId || currentCompanyId}/user/${user?.uid || ""}/settings`}
                className="sidebar-user-menu-item"
                onClick={() => setShowUserMenu(false)}
              >
                <UserCog size={16} strokeWidth={1.75} />
                <span>Inställningar</span>
              </Link>
              <button
                className="sidebar-user-menu-item"
                onClick={() => {
                  setShowUserMenu(false);
                  setShowFeedback(true);
                }}
              >
                <Megaphone size={16} strokeWidth={1.75} />
                <span>Feedback</span>
              </button>
              <button
                className="sidebar-user-menu-item"
                onClick={() => {
                  setShowUserMenu(false);
                  setShowCommunity(true);
                }}
              >
                <WhatsAppIcon size={16} color="currentColor" />
                <span>Community</span>
              </button>
              <div className="sidebar-user-menu-divider" />
              <button
                className="sidebar-user-menu-item"
                onClick={() => {
                  setShowUserMenu(false);
                  onLogout();
                }}
              >
                <LogOutIcon size={16} strokeWidth={1.75} />
                <span>Logga ut</span>
              </button>
            </div>
          )}
        </div>

      </div>

      {/* Route-derived source, not a flat "sidebar": PageHeader carries a
          per-page feedback link, but a few surfaces have bespoke chrome with
          no masthead (TemplateEditor, FeatureDisabledPage, FiscalYearDetails)
          and the unmigrated template pages have none at all. Deriving the
          source here means every page reports usable attribution regardless. */}
      <FeedbackWidget
        show={showFeedback}
        onHide={() => setShowFeedback(false)}
        source={feedbackSource}
      />
      <WhatsAppCommunityModal
        show={showCommunity}
        onHide={() => setShowCommunity(false)}
      />

      {/* Build info modal — tap logo 10 times to reveal */}
      <Modal show={showBuildInfo} onHide={() => setShowBuildInfo(false)} centered size="sm">
        <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
          <Modal.Title style={{ fontSize: '0.875rem', fontWeight: 600 }}>Build Info</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ paddingTop: '0.5rem' }}>
          <BuildInfo />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SideBar;
